import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpenIcon, 
  AcademicCapIcon, 
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const RegisterPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    education_level: '',
    current_institution: '',
    password: '',
    confirm_password: '',
    agree_to_terms: false,
    subscribe_to_updates: false,
    course: '',
    category: 'physical',
    next_of_kin_name: '',
    next_of_kin_phone: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState('');
  const [registeredStudent, setRegisteredStudent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.getCourses();
        setCourses(list);
      } catch (e) {
        console.warn('Failed to load courses', e);
      }
    })();
  }, []);

  const selectedCourse = useMemo(() => (
    courses.find(c => c.id === formData.course) || null
  ), [courses, formData.course]);

  const displayedFee = useMemo(() => {
    const base = Number(selectedCourse?.cost_amount || 0);
    if (!base) return 0;
    return formData.category === 'online' ? base * 0.5 : base;
  }, [selectedCourse, formData.category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!formData.gender) newErrors.gender = 'Please select your gender';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.education_level) newErrors.education_level = 'Education level is required';
    if (!formData.current_institution.trim()) newErrors.current_institution = 'Current institution is required';
    if (!formData.course) newErrors.course = 'Please select a course';
    if (!formData.category) newErrors.category = 'Select a category';
    if (!formData.next_of_kin_name.trim()) newErrors.next_of_kin_name = 'Next of kin name is required';
    if (!formData.next_of_kin_phone.trim()) newErrors.next_of_kin_phone = 'Next of kin phone is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    if (!formData.agree_to_terms) {
      newErrors.agree_to_terms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) return;
    
    setIsLoading(true);
    setIsSubmitting(true);
    setServerMessage('');

    try {
      const payload = {
        full_name: `${formData.first_name} ${formData.last_name}`.trim(),
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender || 'male',
        address: formData.address,
        course: formData.course,
        category: formData.category,
        next_of_kin_name: formData.next_of_kin_name,
        next_of_kin_phone: formData.next_of_kin_phone,
        total_fee: Number(selectedCourse?.cost_amount || 0),
        password: formData.password,
        re_password: formData.confirm_password,
      };

      const res = await api.registerStudent(payload);
      setRegisteredStudent(res);
      setServerMessage(res?.detail || 'Registration successful. Proceed to payment.');
    } catch (error) {
      setErrors({ submit: error.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, color: 'bg-gray-200', text: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
    const texts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    
    return {
      strength: Math.min(strength, 5),
      color: colors[strength - 1] || 'bg-gray-200',
      text: texts[strength - 1] || ''
    };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Background decorations */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-primary-200 rounded-full opacity-20 animate-bounce-subtle"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-secondary-200 rounded-full opacity-20 animate-bounce-subtle" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-accent-200 rounded-full opacity-20 animate-bounce-subtle" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="w-full max-w-4xl z-10 animate-fade-in p-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl mb-6 shadow-lg">
            <BookOpenIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Join <span className="text-primary-600">Aitech</span>
          </h1>
          <p className="text-gray-600 text-lg">
            Start your AI-powered learning journey today
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= stepNumber 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step > stepNumber ? 'bg-primary-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Registration Form */}
        <div className="glass-effect rounded-3xl p-8 shadow-2xl">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 text-primary-700">
              <AcademicCapIcon className="w-6 h-6" />
              <span className="font-semibold text-lg">Student Registration</span>
              <SparklesIcon className="w-5 h-5 text-accent-500" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`input-field ${errors.first_name ? 'border-red-500' : ''}`}
                      placeholder="Enter your first name"
                    />
                    {errors.first_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`input-field ${errors.last_name ? 'border-red-500' : ''}`}
                      placeholder="Enter your last name"
                    />
                    {errors.last_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className={`input-field ${errors.date_of_birth ? 'border-red-500' : ''}`}
                    />
                    {errors.date_of_birth && (
                      <p className="text-red-500 text-sm mt-1">{errors.date_of_birth}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Gender *
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={`input-field ${errors.gender ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Address & Education */}
            {step === 2 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Address & Education</h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label mb-2">Course *</label>
                      <select
                        name="course"
                        value={formData.course}
                        onChange={handleChange}
                        className={`input-field ${errors.course ? 'border-red-500' : ''}`}
                      >
                        <option value="">Select a course</option>
                        {courses.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {errors.course && (
                        <p className="text-red-500 text-sm mt-1">{errors.course}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label mb-2">Study Category *</label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`input-field ${errors.category ? 'border-red-500' : ''}`}
                      >
                        <option value="physical">Physical</option>
                        <option value="online">Online (50% discount)</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                      {errors.category && (
                        <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                    <p className="text-sm text-blue-700">
                      Estimated fee: <span className="font-semibold">{selectedCourse?.cost_currency || 'NGN'} {Number(displayedFee).toLocaleString()}</span>
                      {formData.category === 'online' && (
                        <span className="ml-2 text-xs text-blue-600">(50% online discount applied)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="form-label mb-2">
                      Address *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                      className={`input-field ${errors.address ? 'border-red-500' : ''}`}
                      placeholder="Enter your full address"
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`input-field ${errors.city ? 'border-red-500' : ''}`}
                        placeholder="Enter your city"
                      />
                      {errors.city && (
                        <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label mb-2">
                        State/Province *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className={`input-field ${errors.state ? 'border-red-500' : ''}`}
                        placeholder="Enter your state"
                      />
                      {errors.state && (
                        <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={`input-field ${errors.country ? 'border-red-500' : ''}`}
                        placeholder="Enter your country"
                      />
                      {errors.country && (
                        <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label mb-2">
                        Education Level *
                      </label>
                      <select
                        name="education_level"
                        value={formData.education_level}
                        onChange={handleChange}
                        className={`input-field ${errors.education_level ? 'border-red-500' : ''}`}
                      >
                        <option value="">Select education level</option>
                        <option value="high_school">High School</option>
                        <option value="diploma">Diploma</option>
                        <option value="bachelors">Bachelor's Degree</option>
                        <option value="masters">Master's Degree</option>
                        <option value="phd">PhD</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.education_level && (
                        <p className="text-red-500 text-sm mt-1">{errors.education_level}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label mb-2">
                        Current Institution *
                      </label>
                      <input
                        type="text"
                        name="current_institution"
                        value={formData.current_institution}
                        onChange={handleChange}
                        className={`input-field ${errors.current_institution ? 'border-red-500' : ''}`}
                        placeholder="Enter your current institution"
                      />
                      {errors.current_institution && (
                        <p className="text-red-500 text-sm mt-1">{errors.current_institution}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label mb-2">Next of Kin Name *</label>
                      <input
                        type="text"
                        name="next_of_kin_name"
                        value={formData.next_of_kin_name}
                        onChange={handleChange}
                        className={`input-field ${errors.next_of_kin_name ? 'border-red-500' : ''}`}
                        placeholder="Enter next of kin full name"
                      />
                      {errors.next_of_kin_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.next_of_kin_name}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label mb-2">Next of Kin Phone *</label>
                      <input
                        type="tel"
                        name="next_of_kin_phone"
                        value={formData.next_of_kin_phone}
                        onChange={handleChange}
                        className={`input-field ${errors.next_of_kin_phone ? 'border-red-500' : ''}`}
                        placeholder="Enter next of kin phone"
                      />
                      {errors.next_of_kin_phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.next_of_kin_phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="btn-secondary"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Account Setup */}
            {step === 3 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Account Setup</h3>
                
                <div className="space-y-6">
                  {registeredStudent && (
                    <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                      <p className="text-green-800 text-sm mb-2">{serverMessage}</p>
                      <p className="text-green-900 text-sm">
                        Total fee due: <span className="font-semibold">{selectedCourse?.cost_currency || 'NGN'} {Number(registeredStudent.total_fee || displayedFee).toLocaleString()}</span>
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="form-label mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                        placeholder="Create a strong password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                    )}
                    
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className="mt-2">
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <div
                              key={level}
                              className={`h-2 flex-1 rounded ${
                                level <= passwordStrength.strength 
                                  ? passwordStrength.color 
                                  : 'bg-gray-200'
                              }`}
                            ></div>
                          ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Password strength: {passwordStrength.text}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="form-label mb-2">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        className={`input-field pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="agree_to_terms"
                        checked={formData.agree_to_terms}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        I agree to the{' '}
                        <button type="button" className="text-primary-600 hover:text-primary-500 underline">
                          Terms and Conditions
                        </button>{' '}
                        and{' '}
                        <button type="button" className="text-primary-600 hover:text-primary-500 underline">
                          Privacy Policy
                        </button> *
                      </label>
                    </div>
                    {errors.agree_to_terms && (
                      <p className="text-red-500 text-sm">{errors.agree_to_terms}</p>
                    )}

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="subscribe_to_updates"
                        checked={formData.subscribe_to_updates}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        Subscribe to updates about new courses and learning opportunities
                      </label>
                    </div>
                  </div>
                </div>

                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="btn-secondary"
                  >
                    Previous
                  </button>
                  {!registeredStudent ? (
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`btn-primary ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Registering...
                        </div>
                      ) : (
                        'Register and Proceed'
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={async () => {
                        try {
                          setIsSubmitting(true);
                          const sid = registeredStudent.student_id;
                          const amt = Number(registeredStudent.total_fee || displayedFee || 0);
                          const init = await api.initFlutterwavePayment(sid, amt);
                          const link = init?.data?.link || init?.data?.data?.link;
                          if (link) {
                            window.location.href = link;
                          } else {
                            throw new Error(init?.message || 'Could not initialize payment');
                          }
                        } catch (e) {
                          setErrors({ submit: e.message || 'Payment initialization failed' });
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      className={`btn-primary ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? 'Redirecting...' : 'Pay Now'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          Need help? Contact our support team
        </div>
      </div>
    </div>
  );
};

export default RegisterPage; 